import { Close } from "../../utilities/icons";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: {
    xs: "calc(100vw - 32px)",
    sm: "min(620px, calc(100vw - 48px))",
  },
  maxHeight: "calc(100vh - 32px)",
  bgcolor: "var(--ax-surface)",
  border: "1px solid var(--ax-border)",
  borderRadius: "var(--ax-radius-lg)",
  boxShadow: "var(--ax-shadow-lg)",
  px: { xs: 2.5, sm: 3.5 },
  py: { xs: 2.5, sm: 3 },
  overflowY: "auto",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "var(--ax-border-strong)",
    borderRadius: 999,
  },
};

const labelSx = {
  "& .MuiFormLabel-asterisk": {
    color: "var(--ax-red)",
  },
  fontWeight: 500,
  color: "var(--ax-ink)",
  fontSize: "0.82rem",
  mb: 0.75,
};

const helperTextProps = {
  sx: {
    color: "var(--ax-red)",
    mx: 0,
    mt: 0.5,
    fontSize: "0.74rem",
  },
};

const trackedFields = [
  "FirstName",
  "LastName",
  "EmpId",
  "Contact",
  "Email",
  "JobTitle",
  "EmployeeType",
  "SpaceName",
  "hierarchyLevel",
  "departmentId",
  "team",
  "reportingToId",
  "managerId",
  "teamLeadId",
  "hrId",
];

const requiredFields = [
  "FirstName",
  "LastName",
  "EmpId",
  "Contact",
  "Email",
  "JobTitle",
  "EmployeeType",
  "SpaceName",
];

const hierarchyLevels = ["Admin", "HR", "Manager", "Team Lead", "Employee"];

function EmployeeModal({
  modalOpen,
  handleModalClose,
  handleChange,
  handleSubmit,
  userData,
  formErrors,
  setIsSubmit,
  initialUserData,
  departmentOptions,
  hierarchyOptions,
  handleHierarchyChange,
  handleReportingUserChange,
  buildDepartmentId,
  getUserDisplayName,
  submitting,
}) {
  const [isChanged, setIsChanged] = useState(false);
  const isEditMode = Boolean(initialUserData?._id);
  const modalTitle = isEditMode ? "Update User" : "Add New User";
  const submitLabel = isEditMode ? "Update User" : "Add User";

  useEffect(() => {
    const isDataChanged = trackedFields.some(
      (field) => (userData?.[field] ?? "") !== (initialUserData?.[field] ?? ""),
    );
    setIsChanged(isDataChanged);
  }, [userData, initialUserData]);

  const isAnyFieldEmpty = () => {
    return requiredFields.some((field) => !userData?.[field]);
  };

  const renderUserSelect = ({ label, name, users, value, onChange, helperText }) => (
    <Box>
      <FormLabel sx={labelSx}>{label}</FormLabel>
      <FormControl fullWidth size="small">
        <InputLabel id={`${name}-label`}>
          {users.length ? `Select ${label}` : `No ${label} available`}
        </InputLabel>
        <Select
          labelId={`${name}-label`}
          id={name}
          value={value || ""}
          onChange={onChange}
          disabled={submitting || users.length === 0}
          label={users.length ? `Select ${label}` : `No ${label} available`}
          name={name}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {users.map((user) => (
            <MenuItem key={user.EmpId} value={user.EmpId}>
              {getUserDisplayName(user)} ({user.EmpId})
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{helperText}</FormHelperText>
      </FormControl>
    </Box>
  );

  const level = userData.hierarchyLevel || "Employee";
  const departmentId =
    userData.departmentId || buildDepartmentId(userData.SpaceName || "");

  return (
    <Modal
      open={modalOpen}
      onClose={handleModalClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography
            sx={{
              fontFamily: "var(--ax-font-heading)",
              fontWeight: 500,
              fontSize: "1.15rem",
              letterSpacing: "-0.02em",
              color: "var(--ax-ink)",
            }}
          >
            {modalTitle}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleModalClose}
            disabled={submitting}
            size="small"
            sx={{ color: "var(--ax-ink-2)" }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
        <Typography
          variant="body2"
          sx={{
            mt: 0.5,
            mb: 3,
            color: "var(--ax-ink-2)",
            fontSize: "0.84rem",
            lineHeight: 1.55,
          }}
        >
          Fill in the employee details below and review the required fields
          before submitting.
        </Typography>
        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <Stack spacing={2}>
            <Box>
              <FormLabel required sx={labelSx}>
                First Name
              </FormLabel>
              <TextField
                name="FirstName"
                value={userData.FirstName}
                onChange={handleChange}
                disabled={submitting}
                helperText={formErrors.FirstName}
                FormHelperTextProps={helperTextProps}
                size="small"
                fullWidth
                placeholder="Enter first name"
              />
            </Box>

            <Box>
              <FormLabel required sx={labelSx}>
                Last Name
              </FormLabel>
              <TextField
                name="LastName"
                value={userData.LastName}
                onChange={handleChange}
                disabled={submitting}
                helperText={formErrors.LastName}
                FormHelperTextProps={helperTextProps}
                size="small"
                fullWidth
                placeholder="Enter last name"
              />
            </Box>

            <Box>
              <FormLabel required sx={labelSx}>
                Employee ID
              </FormLabel>
              <TextField
                name="EmpId"
                value={userData.EmpId}
                onChange={handleChange}
                disabled={submitting}
                helperText={formErrors.EmpId}
                FormHelperTextProps={helperTextProps}
                size="small"
                fullWidth
                placeholder="Enter employee ID"
              />
            </Box>

            <Box>
              <FormLabel required sx={labelSx}>
                Contact Number
              </FormLabel>
              <TextField
                name="Contact"
                type="tel"
                value={userData.Contact}
                disabled={submitting}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ""); // remove non-digits
                  if (value.length <= 10) {
                    handleChange({
                      target: { name: "Contact", value },
                    });
                  }
                }}
                helperText={formErrors.Contact}
                FormHelperTextProps={helperTextProps}
                size="small"
                fullWidth
                placeholder="Enter contact number"
                inputProps={{
                  maxLength: 10,
                  inputMode: "numeric", // better mobile keyboard
                  pattern: "[0-9]*",
                }}
              />
            </Box>

            <Box>
              <FormLabel required sx={labelSx}>
                Email
              </FormLabel>
              <TextField
                name="Email"
                value={userData.Email}
                onChange={handleChange}
                disabled={submitting}
                helperText={formErrors.Email}
                FormHelperTextProps={helperTextProps}
                size="small"
                fullWidth
                placeholder="Enter email"
              />
            </Box>

            <Box>
              <FormLabel required sx={labelSx}>
                Job Title
              </FormLabel>
              <TextField
                name="JobTitle"
                value={userData.JobTitle}
                onChange={handleChange}
                disabled={submitting}
                helperText={formErrors.JobTitle}
                FormHelperTextProps={helperTextProps}
                size="small"
                fullWidth
                placeholder="Enter job title"
              />
            </Box>

            <Box>
              <FormLabel required sx={labelSx}>
                Employer Type
              </FormLabel>
              <FormControl fullWidth size="small">
                <InputLabel id="employee-type-label">
                  Select Employment Type
                </InputLabel>
                <Select
                  labelId="employee-type-label"
                  id="employee-type"
                  value={userData.EmployeeType}
                  onChange={handleChange}
                  disabled={submitting}
                  label="Select Employment Type"
                  name="EmployeeType"
                >
                  <MenuItem value={"Full Time"}>Full Time</MenuItem>
                  <MenuItem value={"Part Time"}>Part Time</MenuItem>
                  <MenuItem value={"Internship"}>Internship</MenuItem>
                  <MenuItem value={"Contract"}>Contract</MenuItem>
                </Select>
                <FormHelperText sx={helperTextProps.sx}>
                  {formErrors.EmployeeType}
                </FormHelperText>
              </FormControl>
            </Box>

            <Box>
              <FormLabel required sx={labelSx}>
                Department
              </FormLabel>
              <FormControl fullWidth size="small">
                <InputLabel id="department-label">Select Department</InputLabel>
                <Select
                  labelId="department-label"
                  id="department"
                  value={userData.SpaceName}
                  onChange={handleHierarchyChange}
                  disabled={submitting}
                  label="Select Department"
                  name="SpaceName"
                >
                  {departmentOptions.map((department) => (
                    <MenuItem key={department} value={department}>
                      {department}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText sx={helperTextProps.sx}>
                  {formErrors.SpaceName}
                </FormHelperText>
              </FormControl>
            </Box>

            <Divider sx={{ my: 0.5, borderColor: "var(--ax-border)" }} />

            <Box>
              <Typography
                sx={{
                  fontFamily: "var(--ax-font-heading)",
                  fontWeight: 500,
                  fontSize: "0.96rem",
                  letterSpacing: "-0.015em",
                  color: "var(--ax-ink)",
                }}
              >
                Hierarchy & Reporting
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: "var(--ax-ink-2)",
                  fontSize: "0.82rem",
                  lineHeight: 1.55,
                }}
              >
                Pick the hierarchy role here. Department defaults automatically
                assign the same department's team lead, manager, and HR where
                available.
              </Typography>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <FormLabel required sx={labelSx}>
                  Hierarchy Role
                </FormLabel>
                <FormControl fullWidth size="small">
                  <InputLabel id="hierarchy-level-label">Select Role</InputLabel>
                  <Select
                    labelId="hierarchy-level-label"
                    id="hierarchy-level"
                    value={level}
                    onChange={handleHierarchyChange}
                    disabled={submitting}
                    label="Select Role"
                    name="hierarchyLevel"
                  >
                    {hierarchyLevels.map((role) => (
                      <MenuItem key={role} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: 1 }}>
                <FormLabel sx={labelSx}>Department ID</FormLabel>
                <TextField
                  name="departmentId"
                  value={departmentId}
                  onChange={handleHierarchyChange}
                  disabled={submitting}
                  size="small"
                  fullWidth
                  placeholder="Auto generated from department"
                />
              </Box>
            </Stack>

            {level === "Manager"
              ? renderUserSelect({
                  label: "HR",
                  name: "hrId",
                  users: hierarchyOptions.hrs,
                  value: userData.hrId,
                  onChange: handleReportingUserChange("hr", hierarchyOptions.hrs),
                  helperText: "Managers report to HR.",
                })
              : null}

            {level === "Team Lead"
              ? renderUserSelect({
                  label: "Manager",
                  name: "managerId",
                  users: hierarchyOptions.managers,
                  value: userData.managerId,
                  onChange: handleReportingUserChange(
                    "manager",
                    hierarchyOptions.managers,
                  ),
                  helperText: "Team Leads report to Managers.",
                })
              : null}

            {level === "Employee"
              ? renderUserSelect({
                  label: "Team Lead",
                  name: "teamLeadId",
                  users: hierarchyOptions.teamLeads,
                  value: userData.teamLeadId,
                  onChange: handleReportingUserChange(
                    "teamLead",
                    hierarchyOptions.teamLeads,
                  ),
                  helperText:
                    "Employees are assigned to the selected department's Team Lead.",
                })
              : null}

            {level === "HR"
              ? renderUserSelect({
                  label: "Admin",
                  name: "reportingToId",
                  users: hierarchyOptions.admins,
                  value: userData.reportingToId,
                  onChange: handleReportingUserChange(
                    "reportingTo",
                    hierarchyOptions.admins,
                  ),
                  helperText: "HR users can report to Admin.",
                })
              : null}

            <Box
              sx={{
                border: "1px solid var(--ax-border)",
                borderRadius: "var(--ax-radius-md)",
                p: 1.75,
                bgcolor: "var(--ax-bg-secondary)",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  color: "var(--ax-ink)",
                }}
              >
                Current assignment
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: "var(--ax-ink-2)",
                  fontSize: "0.8rem",
                  lineHeight: 1.6,
                }}
              >
                Reports to: {userData.reportingToName || "Not assigned"} ·
                Manager: {userData.managerName || "Not assigned"} · Team Lead:{" "}
                {userData.teamLeadName || "Not assigned"} · HR:{" "}
                {userData.hrName || "Not assigned"}
              </Typography>
            </Box>

            <Stack
              direction={{ xs: "column-reverse", sm: "row" }}
              justifyContent="flex-end"
              spacing={1.25}
              sx={{ pt: 1 }}
            >
              <Button
                variant="outlined"
                onClick={() => {
                  handleModalClose();
                  setIsSubmit(false);
                }}
                disabled={submitting}
                sx={{ minWidth: { xs: "100%", sm: 110 } }}
              >
                Cancel
              </Button>
              <Button
                disabled={!isChanged || isAnyFieldEmpty() || submitting}
                variant="contained"
                type="submit"
                sx={{ minWidth: { xs: "100%", sm: 140 } }}
              >
                {submitting ? "Saving..." : submitLabel}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Box>
    </Modal>
  );
}

export default EmployeeModal;
